import { useEffect, useRef } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { updateAttractionActive } from '~/services/AttractionsService';

import { attractionQueryKeys } from './attractionQueryKeys';

interface UpdateAttractionActiveData {
  attractionId: string;
  active: boolean;
}

/**
 * Toggles attraction active/inactive. Patches all cached list queries in-place
 * (removes deactivated rows from activeOnly queries). Single delayed refetch
 * after MV refresh to sync.
 */
export function useUpdateAttractionActive() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const refetchTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    return () => clearTimeout(refetchTimer.current);
  }, []);

  return useMutation({
    mutationFn: ({ attractionId, active }: UpdateAttractionActiveData) => updateAttractionActive(attractionId, active),
    onSuccess: (_data, variables) => {
      const allQueries = queryClient.getQueriesData({ queryKey: attractionQueryKeys.all });
      for (const [key, data] of allQueries) {
        if (!data || typeof data !== 'object' || !('result' in data)) continue;
        if (!Array.isArray((data as { result: unknown }).result)) continue;
        const typed = data as { result: Array<{ attractionId: string; active: boolean }>; total?: number };
        const filters = Array.isArray(key) ? key.find((k) => typeof k === 'object' && k !== null) : undefined;
        const isActiveOnlyQuery = !!(filters as Record<string, unknown> | undefined)?.activeOnly;

        if (isActiveOnlyQuery && !variables.active) {
          const filtered = typed.result.filter((item) => item.attractionId !== variables.attractionId);
          queryClient.setQueryData(key, {
            ...typed,
            result: filtered,
            total: (typed.total ?? filtered.length + 1) - 1,
          });
        } else {
          queryClient.setQueryData(key, {
            ...typed,
            result: typed.result.map((item) =>
              item.attractionId === variables.attractionId ? { ...item, active: variables.active } : item,
            ),
          });
        }
      }

      enqueueSnackbar(`Attraction ${variables.active ? 'activated' : 'deactivated'} successfully`, {
        variant: 'success',
      });

      clearTimeout(refetchTimer.current);
      refetchTimer.current = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: attractionQueryKeys.lists() });
      }, 6000);
    },
    onError: (error: Error) => {
      queryClient.invalidateQueries({ queryKey: attractionQueryKeys.all });
      enqueueSnackbar(`Error updating attraction: ${error.message || 'Unknown error'}`, { variant: 'error' });
    },
  });
}
